import React, { useState } from 'react';
import { Box, Paper, Typography, keyframes } from '@mui/material';
import { LocaleSwitcher } from '../../i18n/components/LocaleSwitcher';

interface AuthenticationUiLayoutProps {
  leftContent?: React.ReactNode;
  children: React.ReactNode;
}

interface NodeState {
  id: string;
  title: string;
  category: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  content: React.ReactNode;
}

const flowDash = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -240; }
`;

/* React Flow Interactive Draggable Hiring Process Canvas (Top-to-Bottom Flow) */
const ReactFlowHiringCanvas: React.FC = () => {
  // Initial Nodes Stacked Top-to-Bottom on Left Canvas
  const [nodes, setNodes] = useState<NodeState[]>([
    {
      id: 'node-1',
      title: '1. Job Opening Criteria',
      category: 'Input Node',
      x: 35,
      y: 40,
      width: 250,
      height: 100,
      color: '#059669',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                bgcolor: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                color: '#ffffff',
                fontWeight: 700,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              #
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
              Senior Full-Stack Engineer
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.625rem', color: '#64748b', fontWeight: 500 }}>
            <Box component="span" sx={{ bgcolor: '#f1f5f9', px: 0.75, py: 0.25, borderRadius: '4px' }}>
              React/TS
            </Box>
            <Box component="span" sx={{ bgcolor: '#f1f5f9', px: 0.75, py: 0.25, borderRadius: '4px' }}>
              Node.js
            </Box>
            <Box component="span" sx={{ color: '#059669', fontWeight: 700 }}>
              Active
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      id: 'node-2',
      title: '2. AI Skill Assessment',
      category: 'Process Node',
      x: 35,
      y: 185,
      width: 250,
      height: 100,
      color: '#10b981',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#475569', fontWeight: 500 }}>
              AI Score Threshold
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 800,
                color: '#047857',
                bgcolor: '#ecfdf5',
                border: '1px solid rgba(167,243,208,0.6)',
                px: 1,
                py: 0.25,
                borderRadius: '8px',
              }}
            >
              94.8%
            </Typography>
          </Box>
          <Box sx={{ width: '100%', height: 6, bgcolor: '#f1f5f9', borderRadius: '9999px', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', bgcolor: '#10b981', borderRadius: '9999px', width: '94.8%' }} />
          </Box>
        </Box>
      ),
    },
    {
      id: 'node-3',
      title: '3. Candidate Evaluation',
      category: 'Evaluation Node',
      x: 35,
      y: 330,
      width: 250,
      height: 100,
      color: '#047857',
      content: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
              Top Candidates
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', color: '#64748b' }}>
              Verified Skill Profiles
            </Typography>
          </Box>
          <Typography
            component="span"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#047857',
              bgcolor: '#ecfdf5',
              border: '1px solid rgba(167,243,208,0.6)',
              px: 1.25,
              py: 0.5,
              borderRadius: '8px',
            }}
          >
            14 Selected
          </Typography>
        </Box>
      ),
    },
    {
      id: 'node-4',
      title: '4. Hiring Output',
      category: 'Output Node',
      x: 35,
      y: 475,
      width: 250,
      height: 145,
      color: '#059669',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Offer Issued</Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.625rem',
                color: '#059669',
                fontWeight: 700,
                bgcolor: '#ecfdf5',
                px: 0.75,
                py: 0.25,
                borderRadius: '4px',
              }}
            >
              Hired
            </Typography>
          </Box>
          <Box
            sx={{
              height: 64,
              width: '100%',
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid rgba(226,232,240,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                opacity: 0.9,
              }}
            >
              <Box sx={{ width: 20, height: 20, borderRadius: '6px', bgcolor: '#059669', transform: 'rotate(12deg)', boxShadow: 1 }} />
              <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: '#047857', transform: 'rotate(-6deg)', boxShadow: 2 }} />
              <Box sx={{ width: 16, height: 16, borderRadius: '6px', bgcolor: '#0d9488', transform: 'rotate(45deg)', boxShadow: 1 }} />
              <Box sx={{ width: 24, height: 24, borderRadius: '6px', bgcolor: '#047857', transform: 'rotate(-12deg)', boxShadow: 2 }} />
            </Box>
          </Box>
        </Box>
      ),
    },
  ]);

  // Dragging State
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const node = nodes.find((n) => n.id === id);
    if (!node) return;
    setDraggingId(id);
    setDragOffset({
      x: e.clientX - node.x,
      y: e.clientY - node.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingId) return;
    setNodes((prev) =>
      prev.map((n) => {
        if (n.id === draggingId) {
          return {
            ...n,
            x: Math.max(10, Math.min(window.innerWidth - n.width - 10, e.clientX - dragOffset.x)),
            y: Math.max(10, Math.min(window.innerHeight - n.height - 10, e.clientY - dragOffset.y)),
          };
        }
        return n;
      })
    );
  };

  const handleMouseUp = () => {
    setDraggingId(null);
  };

  const n1 = nodes.find((n) => n.id === 'node-1') || nodes[0];
  const n2 = nodes.find((n) => n.id === 'node-2') || nodes[1];
  const n3 = nodes.find((n) => n.id === 'node-3') || nodes[2];
  const n4 = nodes.find((n) => n.id === 'node-4') || nodes[3];

  const p1Bottom = { x: n1.x + n1.width / 2, y: n1.y + n1.height };
  const p2Top = { x: n2.x + n2.width / 2, y: n2.y };
  const p2Bottom = { x: n2.x + n2.width / 2, y: n2.y + n2.height };
  const p3Top = { x: n3.x + n3.width / 2, y: n3.y };
  const p3Bottom = { x: n3.x + n3.width / 2, y: n3.y + n3.height };
  const p4Top = { x: n4.x + n4.width / 2, y: n4.y };

  const path1 = `M ${p1Bottom.x} ${p1Bottom.y} C ${p1Bottom.x} ${p1Bottom.y + 25}, ${p2Top.x} ${p2Top.y - 25}, ${p2Top.x} ${p2Top.y}`;
  const path2 = `M ${p2Bottom.x} ${p2Bottom.y} C ${p2Bottom.x} ${p2Bottom.y + 25}, ${p3Top.x} ${p3Top.y - 25}, ${p3Top.x} ${p3Top.y}`;
  const path3 = `M ${p3Bottom.x} ${p3Bottom.y} C ${p3Bottom.x} ${p3Bottom.y + 25}, ${p4Top.x} ${p4Top.y - 25}, ${p4Top.x} ${p4Top.y}`;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        userSelect: 'none',
        zIndex: 0,
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* 1. Dot Matrix Canvas Grid */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.45,
          pointerEvents: 'none',
          backgroundImage: 'radial-gradient(#64748b 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* 2. Soft Glowing Ambient Aura Blobs */}
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          left: 40,
          width: 550,
          height: 550,
          bgcolor: 'rgba(16, 185, 129, 0.15)',
          borderRadius: '50%',
          filter: 'blur(140px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          width: 600,
          height: 600,
          bgcolor: 'rgba(4, 120, 87, 0.15)',
          borderRadius: '50%',
          filter: 'blur(160px)',
          pointerEvents: 'none',
        }}
      />

      {/* 3. SVG Bezier Connecting Paths */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          fill: 'none',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <path
          d={path1}
          stroke='#059669'
          strokeWidth='2.5'
          strokeOpacity='0.9'
          strokeDasharray='6 6'
          style={{ animation: `${flowDash} 12s linear infinite` }}
        />
        <path
          d={path2}
          stroke='#10b981'
          strokeWidth='2.5'
          strokeOpacity='0.9'
          strokeDasharray='6 6'
          style={{ animation: `${flowDash} 8s linear infinite` }}
        />
        <path
          d={path3}
          stroke='#047857'
          strokeWidth='2.5'
          strokeOpacity='0.9'
          strokeDasharray='6 6'
          style={{ animation: `${flowDash} 12s linear infinite` }}
        />
      </svg>

      {/* 4. Interactive Draggable React Flow Node Cards */}
      {nodes.map((node) => {
        const isDragging = draggingId === node.id;
        return (
          <Paper
            key={node.id}
            elevation={isDragging ? 8 : 3}
            onMouseDown={(e) => handleMouseDown(e, node.id)}
            sx={{
              display: { xs: 'none', lg: 'flex' },
              flexDirection: 'column',
              position: 'absolute',
              left: `${node.x}px`,
              top: `${node.y}px`,
              width: `${node.width}px`,
              p: 1.75,
              borderRadius: '16px',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(226, 232, 240, 0.9)',
              cursor: isDragging ? 'grabbing' : 'grab',
              zIndex: isDragging ? 30 : 20,
              transform: isDragging ? 'scale(1.04)' : 'none',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                transform: isDragging ? 'scale(1.04)' : 'scale(1.02)',
              },
            }}
          >
            {/* Top Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #f1f5f9',
                pb: 0.75,
                mb: 1,
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {node.title}
              </Typography>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#10b981',
                }}
              />
            </Box>

            {/* Node Content */}
            <Box sx={{ pointerEvents: 'none' }}>{node.content}</Box>

            {/* Handles */}
            {node.id !== 'node-1' && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: -7,
                  transform: 'translateX(-50%)',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#ffffff',
                  border: '2px solid #059669',
                  boxShadow: '0 0 8px rgba(5,150,105,0.5)',
                  zIndex: 30,
                }}
              />
            )}
            {node.id !== 'node-4' && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: -7,
                  transform: 'translateX(-50%)',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#ffffff',
                  border: '2px solid #059669',
                  boxShadow: '0 0 8px rgba(5,150,105,0.5)',
                  zIndex: 30,
                }}
              />
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

/* AI HireTalent Showcase Panel for Left Hero Column */
export const AiTalentShowcase: React.FC = () => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      p: { xs: 3, sm: 4 },
      color: '#ffffff',
      position: 'relative',
      zIndex: 10,
      userSelect: 'none',
    }}
  >
    {/* Top Header Badge & Tagline */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: '9999px',
          bgcolor: 'rgba(16, 185, 129, 0.2)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          color: '#6ee7b7',
          fontSize: '0.75rem',
          fontWeight: 600,
          backdropFilter: 'blur(8px)',
          alignSelf: 'flex-start',
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#34d399' }} />
        <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
          AI Talent Intelligence
        </Typography>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.875rem' },
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          color: '#ffffff',
        }}
      >
        Smart Hiring. <br />
        <Box
          component="span"
          sx={{
            background: 'linear-gradient(90deg, #34d399 0%, #5eead4 50%, #a7f3d0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Evaluated with AI.
        </Box>
      </Typography>

      <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', lineHeight: 1.6, maxWidth: 300 }}>
        Discover, assess, and hire top 1% tech candidates seamlessly with automated evaluation pipelines.
      </Typography>
    </Box>

    {/* Center Candidate Intelligence Showcase Card */}
    <Box
      sx={{
        my: 3,
        p: 2,
        borderRadius: '16px',
        bgcolor: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'rgba(16, 185, 129, 0.4)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, pb: 1.25, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #2dd4bf 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.75rem',
              boxShadow: 1,
            }}
          >
            AR
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>
              Alex Rivera
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', color: 'rgba(110, 231, 183, 0.85)', fontWeight: 500 }}>
              Senior Systems Architect
            </Typography>
          </Box>
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 800,
            color: '#34d399',
            bgcolor: 'rgba(16, 185, 129, 0.2)',
            px: 1,
            py: 0.25,
            borderRadius: '8px',
            border: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          98.4% Match
        </Typography>
      </Box>

      {/* Progress Bars */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, fontSize: '0.6875rem' }}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#e2e8f0', fontSize: '0.625rem', fontWeight: 500, mb: 0.5 }}>
            <Typography component="span" sx={{ fontSize: '0.625rem' }}>System Architecture</Typography>
            <Typography component="span" sx={{ fontSize: '0.625rem', color: '#6ee7b7', fontWeight: 700 }}>99%</Typography>
          </Box>
          <Box sx={{ width: '100%', height: 6, bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: '9999px', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: '99%', background: 'linear-gradient(90deg, #10b981 0%, #2dd4bf 100%)', borderRadius: '9999px' }} />
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#e2e8f0', fontSize: '0.625rem', fontWeight: 500, mb: 0.5 }}>
            <Typography component="span" sx={{ fontSize: '0.625rem' }}>Coding & Algorithms</Typography>
            <Typography component="span" sx={{ fontSize: '0.625rem', color: '#6ee7b7', fontWeight: 700 }}>96%</Typography>
          </Box>
          <Box sx={{ width: '100%', height: 6, bgcolor: 'rgba(15, 23, 42, 0.8)', borderRadius: '9999px', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: '96%', background: 'linear-gradient(90deg, #10b981 0%, #2dd4bf 100%)', borderRadius: '9999px' }} />
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Bottom Live Metrics Row */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        pt: 2,
        color: '#cbd5e1',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.2 }}>2,490+</Typography>
        <Typography sx={{ fontSize: '0.625rem', color: '#94a3b8' }}>Evaluated</Typography>
      </Box>
      <Box sx={{ height: 24, width: '1px', bgcolor: 'rgba(255,255,255,0.15)' }} />
      <Box>
        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#34d399', lineHeight: 1.2 }}>99.2%</Typography>
        <Typography sx={{ fontSize: '0.625rem', color: '#94a3b8' }}>Precision</Typography>
      </Box>
      <Box sx={{ height: 24, width: '1px', bgcolor: 'rgba(255,255,255,0.15)' }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: '0.75rem',
          color: '#6ee7b7',
          fontWeight: 500,
          bgcolor: 'rgba(6, 78, 59, 0.6)',
          px: 1.25,
          py: 0.5,
          borderRadius: '9999px',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34d399' }} />
        <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600 }}>Engine Active</Typography>
      </Box>
    </Box>
  </Box>
);

export const AuthenticationUiLayout: React.FC<AuthenticationUiLayoutProps> = ({
  leftContent,
  children,
}) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3, md: 5 },
        bgcolor: '#f8fafc',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'inherit',
      }}
    >
      {/* Top right language switcher */}
      <Box sx={{ position: 'absolute', top: 20, right: 24, zIndex: 30 }}>
        <LocaleSwitcher variant='inline' />
      </Box>

      {/* Interactive React Flow Hiring Process Canvas Background */}
      <ReactFlowHiringCanvas />

      {/* Main Container Card (Split 2-Column Layout) */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 1020,
          minHeight: 580,
          my: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: '32px',
          bgcolor: '#ffffff',
          boxShadow: '0 25px 70px -15px rgba(0,0,0,0.14)',
          border: '1px solid rgba(226,232,240,0.9)',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
        }}
      >
        {/* Left Column: AI HireTalent Showcase Section */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            background: 'linear-gradient(135deg, #051c14 0%, #08291d 50%, #03130d 100%)',
            minHeight: { xs: 300, md: 580 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            userSelect: 'none',
            overflow: 'hidden',
            borderRight: '1px solid rgba(6, 78, 59, 0.4)',
          }}
        >
          {leftContent || <AiTalentShowcase />}
        </Box>

        {/* Right Column: Clean White Form Section */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            bgcolor: '#ffffff',
            p: { xs: 4, sm: 5, md: 6 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto' }}>{children}</Box>
        </Box>
      </Paper>
    </Box>
  );
};
